/** Rollout policy for public site-diligence providers.
 *
 * Public soil, mineral-occurrence and National Register screens run automatically
 * after live Holly Ridge provider acceptance on 2026-09-22.
 * An explicit flag enables or disables the automatic path in any environment.
 * Explicit selected-provider reruns are handled by the dispatcher and are not
 * blocked by this default policy.
 */

export const NRCS_SSURGO_ENABLED_FLAG = 'VITE_NRCS_SSURGO_ENABLED';
export const NRCS_SSURGO_ENABLED_SERVER_FLAG = 'NRCS_SSURGO_ENABLED';
export const SITE_DILIGENCE_PROVIDERS_FLAG = 'VITE_SITE_DILIGENCE_PROVIDERS';

export interface NrcsSsurgoRolloutContext {
  flagValue?: string;
  isProduction: boolean;
  isTest: boolean;
}

export interface SiteDiligencePublicScreensContext {
  flagValue?: string;
  isDevelopment: boolean;
  isTest: boolean;
}

function parseExplicitBoolean(raw: string | undefined): boolean | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return null;
}

/** Pure resolver exported for deterministic rollout tests. */
export function resolveNrcsSsurgoDefaultEnabled(
  context: NrcsSsurgoRolloutContext,
): boolean {
  const explicit = parseExplicitBoolean(context.flagValue);
  if (explicit != null) return explicit;
  return true;
}

function runtimeEnv(): Record<string, unknown> | undefined {
  return typeof import.meta !== 'undefined' && import.meta.env != null
    ? import.meta.env as Record<string, unknown>
    : undefined;
}

/** Pure resolver for the shared public-screen rollout policy. */
export function resolveSiteDiligencePublicScreensEnabled(
  context: SiteDiligencePublicScreensContext,
): boolean {
  const explicit = parseExplicitBoolean(context.flagValue);
  if (explicit != null) return explicit;
  return true;
}

/**
 * Public site-diligence feeds default on for screening in server and browser
 * builds. An explicit false flag remains the operational rollback.
 *
 * `import.meta.env` does not exist in the CommonJS server bundle. Access must
 * therefore go through `runtimeEnv()` rather than dereferencing it directly.
 */
export function siteDiligencePublicScreensEnabled(): boolean {
  const meta = runtimeEnv();
  const processEnv = typeof process !== 'undefined' ? process.env : undefined;
  const viteFlag = typeof meta?.[SITE_DILIGENCE_PROVIDERS_FLAG] === 'string'
    ? String(meta[SITE_DILIGENCE_PROVIDERS_FLAG])
    : undefined;
  const serverFlag = processEnv?.[SITE_DILIGENCE_PROVIDERS_FLAG];
  const mode = typeof meta?.MODE === 'string' ? String(meta.MODE) : processEnv?.NODE_ENV;
  return resolveSiteDiligencePublicScreensEnabled({
    flagValue: viteFlag ?? serverFlag,
    isDevelopment: meta?.DEV === true || mode === 'development',
    isTest: mode === 'test' || processEnv?.VITEST === 'true',
  });
}

/** Runtime default used only for automatic/recommended provider selection. */
export function isNrcsSsurgoDefaultEnabled(): boolean {
  const meta = runtimeEnv();
  const processEnv = typeof process !== 'undefined' ? process.env : undefined;
  const viteFlag = typeof meta?.[NRCS_SSURGO_ENABLED_FLAG] === 'string'
    ? String(meta[NRCS_SSURGO_ENABLED_FLAG])
    : undefined;
  const serverFlag = processEnv?.[NRCS_SSURGO_ENABLED_SERVER_FLAG]
    ?? processEnv?.[NRCS_SSURGO_ENABLED_FLAG];
  const mode = typeof meta?.MODE === 'string' ? String(meta.MODE) : processEnv?.NODE_ENV;
  return resolveNrcsSsurgoDefaultEnabled({
    flagValue: viteFlag ?? serverFlag,
    isProduction: meta?.PROD === true || mode === 'production' || processEnv?.NODE_ENV === 'production',
    isTest: mode === 'test' || processEnv?.NODE_ENV === 'test' || processEnv?.VITEST === 'true',
  });
}

/** Dispatch rule: an explicit selected-provider request is allowed even while
 * automatic production collection is gated off. */
export function shouldDispatchNrcsSsurgo(
  selectedProviders: readonly string[] | undefined,
  defaultEnabled = isNrcsSsurgoDefaultEnabled(),
): boolean {
  return selectedProviders != null
    ? selectedProviders.includes('nrcs-soil-survey')
    : defaultEnabled;
}
